import { lessonCategoriesSearch } from "@/lesson-categories/search";
import { diagnosticsSearch } from "@/diagnostics/search";
import { loadOnboardingRequirement } from "@/onboarding/api";
import { queryClient } from "@/lib/query-client";
import { usePreferencesStore } from "@/preferences-store";
import { useApplyTheme } from "@/theme";
import { withListSearch } from "@/lib/list-search";
import { eventsSearch } from "@/events/dates";
import { adminModerationSearch } from "@/admin-moderation-search";
import { adminTagsSearch } from "@/admin-tags-search";
import { rememberCommunicationRoute } from "@/communications/navigation";
import { rememberFormRoute } from "@/forms/navigation";
// src/router.tsx
//
// Roteamento autenticado: sessão e política publicada pelo backend antes do render.
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  lazyRouteComponent,
  type RouteComponent,
} from "@organizacaox/lai-design-system/router";
import { TooltipProvider, Toaster } from "@organizacaox/lai-design-system";
import { AppLoadingScreen, RouteErrorScreen } from "@/lib/app-states";
import { directIntegrationSearch } from "@/integrations/direct-integration-navigation";
import { ingestionLogsSearch } from "@/integrations/ingestion-logs-navigation";
import { routePreloadOptions } from "@/lib/route-preload";
import { trackHistory } from "@/lib/history-trail";
import { RouteLoadingScreen } from "@/lib/route-content";
import { courseLearningSearch } from "@/courses/learning-navigation";
import { courseCatalogSearch } from "@/courses/catalog-navigation";
import { feedSearch, spaceFeedSearch } from "@/community-navigation";
import { loadRouteSession } from "@/lib/route-session";
import { loadShellNavigation } from "@/lib/shell-navigation-loading";
import { SessionGuard } from "@/lib/session-guard";
import { canEnterRoute } from "@/lib/route-policy";
import { programsSearch } from "@/programs-navigation";
import {
  brandingSearch,
  redirectLegacyShowcase,
  redirectLegacyShowcases,
} from "@/showcases/branding-navigation";

const EventsScreen = lazyRouteComponent(() => import("@/events/screens"), "EventsScreen");
const EventScreen = lazyRouteComponent(() => import("@/events/screens"), "EventScreen");
const EventsManagement = lazyRouteComponent(() => import("@/events/screens"), "EventsManagement");
const CommunicationsManagement = lazyRouteComponent(
  () => import("@/communications/screens"),
  "CommunicationsManagement",
);
const CommunicationScreen = lazyRouteComponent(
  () => import("@/communications/screens"),
  "CommunicationScreen",
);
const DiagnosisScreen = lazyRouteComponent(
  () => import("@/onboarding/diagnosis-screen"),
  "DiagnosisScreen",
);
const OnboardingPage = lazyRouteComponent(() => import("@/onboarding/page"), "OnboardingPage");
const FormsScreen = lazyRouteComponent(() => import("@/forms/screens"), "FormsScreen");
const FormScreen = lazyRouteComponent(() => import("@/forms/screens"), "FormScreen");
const PublicFormScreen = lazyRouteComponent(() => import("@/forms/screens"), "PublicFormScreen");
const publicFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/f/$slug",
  component: PublicFormScreen,
});

const MyBusinessScreen = lazyRouteComponent(
  () => import("@/billing/deals-screen"),
  "MyBusinessScreen",
);
const AppLayout = lazyRouteComponent(() => import("@/app-layout"), "AppLayout");
const GettingStartedScreen = lazyRouteComponent(
  () => import("@/getting-started/screen"),
  "GettingStartedScreen",
);
const HomeScreen = lazyRouteComponent(() => import("@/home-screen"), "HomeScreen");
const ProgramsScreen = lazyRouteComponent(() => import("@/programs-screen"), "ProgramsScreen");
const ProgramViewScreen = lazyRouteComponent(
  () => import("@/program-view-screen"),
  "ProgramViewScreen",
);
const CohortsScreen = lazyRouteComponent(() => import("@/cohorts-screen"), "CohortsScreen");
const MenteesScreen = lazyRouteComponent(() => import("@/mentees-screen"), "MenteesScreen");
const CourseLibraryScreen = lazyRouteComponent(
  () => import("@/courses-screen"),
  "CourseLibraryScreen",
);
const CoursesScreen = lazyRouteComponent(() => import("@/courses-screen"), "CoursesScreen");
const CoursePlayerScreen = lazyRouteComponent(
  () => import("@/course-player-screen"),
  "CoursePlayerScreen",
);
const SpacesScreen = lazyRouteComponent(() => import("@/spaces-screen"), "SpacesScreen");
const SpacesDetailScreen = lazyRouteComponent(
  () => import("@/spaces-detail-screen"),
  "SpacesDetailScreen",
);
const CourseBuilderScreen = lazyRouteComponent(
  () => import("@/course-builder-screen"),
  "CourseBuilderScreen",
);
const PostScreen = lazyRouteComponent(() => import("@/post-screen"), "PostScreen");
const FeedScreen = lazyRouteComponent(() => import("@/feed-screen"), "FeedScreen");
const NotificationsScreen = lazyRouteComponent(
  () => import("@/notifications-screen"),
  "NotificationsScreen",
);
const SavedPostsScreen = lazyRouteComponent(
  () => import("@/saved-posts-screen"),
  "SavedPostsScreen",
);
const ProfileScreen = lazyRouteComponent(() => import("@/profile-screen"), "ProfileScreen");
const AccountProfileScreen = lazyRouteComponent(
  () => import("@/account-profile-screen"),
  "AccountProfileScreen",
);
const MyAccessBadgesScreen = lazyRouteComponent(
  () => import("@/access-badges/my-access-badges-screen"),
  "MyAccessBadgesScreen",
);
const SettingsScreen = lazyRouteComponent(() => import("@/settings-screen"), "SettingsScreen");
const InstallScreen = lazyRouteComponent(() => import("@/pwa/install-screen"), "InstallScreen");
const SupportHelpScreen = lazyRouteComponent(
  () => import("@/support/help-screen"),
  "SupportHelpScreen",
);
const SupportHelpSectionScreen = lazyRouteComponent(
  () => import("@/support/help-screen"),
  "SupportHelpSectionScreen",
);
const SupportTicketsScreen = lazyRouteComponent(
  () => import("@/support/tickets-screen"),
  "SupportTicketsScreen",
);
const SupportTicketScreen = lazyRouteComponent(
  () => import("@/support/ticket-screen"),
  "SupportTicketScreen",
);
const MessagesScreen = lazyRouteComponent(() => import("@/messages-screen"), "MessagesScreen");
const ShowcaseScreen = lazyRouteComponent(() => import("@/showcase-screen"), "ShowcaseScreen");
const JourneyScreen = lazyRouteComponent(() => import("@/journey-screen"), "JourneyScreen");
const GamificationScreen = lazyRouteComponent(
  () => import("@/gamification-screen"),
  "GamificationScreen",
);
const AdminGamificationBadgesScreen = lazyRouteComponent(
  () => import("@/admin-gamification-badges-screen"),
  "AdminGamificationBadgesScreen",
);
const WalletScreen = lazyRouteComponent(() => import("./wallet-screen"), "WalletScreen");
const AdminDashboardScreen = lazyRouteComponent(
  () => import("@/admin-dashboard-screen"),
  "AdminDashboardScreen",
);
const AdminFeaturesScreen = lazyRouteComponent(
  () => import("@/admin-features-screen"),
  "AdminFeaturesScreen",
);
const AdminBrandingRoute = lazyRouteComponent(
  () => import("@/admin-branding-screen"),
  "AdminBrandingRoute",
);
const LessonCategoriesScreen = lazyRouteComponent(
  () => import("@/lesson-categories/screen"),
  "LessonCategoriesScreen",
);
const DiagnosticsScreen = lazyRouteComponent(
  () => import("@/diagnostics/screen"),
  "DiagnosticsScreen",
);
const DiagnosticEditorRoute = lazyRouteComponent(
  () => import("@/diagnostics/editor"),
  "DiagnosticEditorRoute",
);
const DiagnosticViewRoute = lazyRouteComponent(
  () => import("@/diagnostics/view"),
  "DiagnosticViewRoute",
);
const AdminTagsScreen = lazyRouteComponent(() => import("@/admin-tags-screen"), "AdminTagsScreen");
const AdminAccessBadgesScreen = lazyRouteComponent(
  () => import("@/access-badges/access-badges-list-screen"),
  "AdminAccessBadgesScreen",
);
const AdminAccessBadgeDetailScreen = lazyRouteComponent(
  () => import("@/access-badges/access-badge-detail-screen"),
  "AdminAccessBadgeDetailScreen",
);
const ShowcaseBuilderScreen = lazyRouteComponent(
  () => import("@/showcases/showcase-builder-screen"),
  "ShowcaseBuilderScreen",
);
const ShowcasePreviewScreen = lazyRouteComponent(
  () => import("@/showcases/showcase-preview-screen"),
  "ShowcasePreviewScreen",
);
const AdminAdsScreen = lazyRouteComponent(() => import("@/admin-ads-screen"), "AdminAdsScreen");
const AdminModerationScreen = lazyRouteComponent(
  () => import("@/admin-moderation-screen"),
  "AdminModerationScreen",
);
const AdminAuditScreen = lazyRouteComponent(
  () => import("@/admin-audit-screen"),
  "AdminAuditScreen",
);
const AdminLessonCommentsScreen = lazyRouteComponent(
  () => import("@/admin-lesson-comments-screen"),
  "AdminLessonCommentsScreen",
);
const AdminUsersScreen = lazyRouteComponent(
  () => import("@/admin-users-screen"),
  "AdminUsersScreen",
);
const AdminDomainsScreen = lazyRouteComponent(
  () => import("@/admin-domains-screen"),
  "AdminDomainsScreen",
);
const AdminInsightsScreen = lazyRouteComponent(
  () => import("@/admin-insights-screen"),
  "AdminInsightsScreen",
);
const AdminUserScoresScreen = lazyRouteComponent(
  () => import("@/admin-user-scores-screen"),
  "AdminUserScoresScreen",
);
const AdminIntegrationsScreen = lazyRouteComponent(
  () => import("@/admin-integrations-screen"),
  "AdminIntegrationsScreen",
);
const IntegrationsMarketplaceScreen = lazyRouteComponent(
  () => import("@/integrations/marketplace-screen"),
  "IntegrationsMarketplaceScreen",
);
const IngestionLogsScreen = lazyRouteComponent(
  () => import("@/integrations/ingestion-logs-screen"),
  "IngestionLogsScreen",
);
const DirectIntegrationRoute = lazyRouteComponent(
  () => import("@/integrations/direct-integration-screen"),
  "DirectIntegrationRoute",
);
const AdminInstitutionalScreen = lazyRouteComponent(
  () => import("@/admin-institutional-screen"),
  "AdminInstitutionalScreen",
);
const PageViewScreen = lazyRouteComponent(() => import("@/page-view-screen"), "PageViewScreen");
const PublicPageViewScreen = lazyRouteComponent(
  () => import("@/public-page-view-screen"),
  "PublicPageViewScreen",
);
const AnalyticsScreen = lazyRouteComponent(() => import("@/analytics-screen"), "AnalyticsScreen");
const OutcomesScreen = lazyRouteComponent(() => import("@/outcomes-screen"), "OutcomesScreen");
const ExecutionScreen = lazyRouteComponent(() => import("@/execution-screen"), "ExecutionScreen");
const DiagnosisPersonScreen = lazyRouteComponent(
  () => import("@/diagnosis-analytics/person-screen"),
  "DiagnosisPersonScreen",
);
const CourseCertificateScreen = lazyRouteComponent(
  () => import("@/certificates/course-certificate-screen"),
  "CourseCertificateScreen",
);
const CourseViewScreen = lazyRouteComponent(
  () => import("@/course-view-screen"),
  "CourseViewScreen",
);
const CertificateModelScreen = lazyRouteComponent(
  () => import("@/certificates/certificate-model-screen"),
  "CertificateModelScreen",
);
const CertificateSettingsScreen = lazyRouteComponent(
  () => import("@/certificates/certificate-settings-screen"),
  "CertificateSettingsScreen",
);
const MyCertificatesScreen = lazyRouteComponent(
  () => import("@/certificates/my-certificates-screen"),
  "MyCertificatesScreen",
);
const PublicCertificateScreen = lazyRouteComponent(
  () => import("@/certificates/public-certificate-screen"),
  "PublicCertificateScreen",
);
const LoginScreen = lazyRouteComponent(() => import("@/login-screen"), "LoginScreen");
const JoinScreen = lazyRouteComponent(() => import("@/join-screen"), "JoinScreen");
const GoogleCallbackScreen = lazyRouteComponent(
  () => import("@/google-callback-screen"),
  "GoogleCallbackScreen",
);
const GoogleStartScreen = lazyRouteComponent(
  () => import("@/google-handoff-screens"),
  "GoogleStartScreen",
);
const GoogleHandoffScreen = lazyRouteComponent(
  () => import("@/google-handoff-screens"),
  "GoogleHandoffScreen",
);
const GoogleCompleteScreen = lazyRouteComponent(
  () => import("@/google-handoff-screens"),
  "GoogleCompleteScreen",
);
const NoAccessScreen = lazyRouteComponent(() => import("@/no-access-screen"), "NoAccessScreen");
const PrivacyPolicyScreen = lazyRouteComponent(
  () => import("@/legal/legal-document-screen"),
  "PrivacyPolicyScreen",
);
const TermsOfUseScreen = lazyRouteComponent(
  () => import("@/legal/legal-document-screen"),
  "TermsOfUseScreen",
);
const CompanyScreen = lazyRouteComponent(() => import("@/company-screen"), "CompanyScreen");
const RolesScreen = lazyRouteComponent(() => import("@/roles-screen"), "RolesScreen");

const ChargeReturnScreen = lazyRouteComponent(
  () => import("@/billing/charge-return-screen"),
  "ChargeReturnScreen",
);
const ChangePasswordScreen = lazyRouteComponent(
  () => import("@/change-password-screen"),
  "ChangePasswordScreen",
);
const CompleteProfileScreen = lazyRouteComponent(
  () => import("@/complete-profile-screen"),
  "CompleteProfileScreen",
);
const ResetPasswordScreen = lazyRouteComponent(
  () => import("@/reset-password-screen"),
  "ResetPasswordScreen",
);
const OAuthConsentScreen = lazyRouteComponent(
  () => import("@/oauth-consent-screen"),
  "OAuthConsentScreen",
);
const PurchaseScreen = lazyRouteComponent(
  () => import("@/billing/purchase-page"),
  "PurchaseScreen",
);
const PurchasesScreen = lazyRouteComponent(
  () => import("@/billing/refunds-screen"),
  "PurchasesScreen",
);
const RefundsScreen = lazyRouteComponent(() => import("@/billing/refunds-screen"), "RefundsScreen");
const SalesScreen = lazyRouteComponent(() => import("@/billing/sales-screen"), "SalesScreen");

function RootLayout() {
  useApplyTheme();
  return (
    <TooltipProvider delay={200}>
      <Outlet />
      <Toaster position="top-right" richColors />
    </TooltipProvider>
  );
}
const rootRoute = createRootRoute({ component: RootLayout });

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: LoginScreen,
});

const installRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/install",
  component: InstallScreen,
});

// Volta do Google (back#256): a sessão já está aberta; ativa a marca e segue.
const googleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/google",
  component: GoogleCallbackScreen,
});

// Login com Google no subdomínio de marca (repasse de sessão, back#391):
// start e handoff rodam no endereço principal; complete no subdomínio.
const googleStartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/google/start",
  component: GoogleStartScreen,
});
const googleHandoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/google/handoff",
  component: GoogleHandoffScreen,
});
const googleCompleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/google/complete",
  component: GoogleCompleteScreen,
});

// Conta sem nenhuma marca (ex.: criada pelo Google). Fora do `_app`, como a
// troca de senha, para não entrar em loop com a própria guarda.
const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/no-access",
  component: NoAccessScreen,
});

// Documentos legais da plataforma (back#366): públicos, linkados no login e na
// tela de consentimento do Google.
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacidade",
  component: PrivacyPolicyScreen,
});
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/termos",
  component: TermsOfUseScreen,
});

const lifecycleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/data-lifecycle",
  component: lazyRouteComponent(() => import("@/lifecycle/lifecycle-screen"), "LifecycleScreen"),
});

const publicPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/public/p/$slug",
  component: PublicPageViewScreen,
});

const purchaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/buy/$offerId",
  component: PurchaseScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    order:
      typeof search.order === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(search.order)
        ? search.order
        : undefined,
    canceled: search.canceled === true || search.canceled === "true",
  }),
});

// Validação pública do certificado (front#121): quem recebe o documento confere
// aqui, sem conta. O código é a credencial.
const certificatePublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/certificado/$code",
  component: PublicCertificateScreen,
});

// Auto-inscrição por link de convite — pública (sem guarda de sessão): o
// mentorado ainda não tem conta nem organização quando abre o link.
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join/$token",
  component: JoinScreen,
});

// Troca de senha obrigatória (1º acesso). Fora da guarda `_app` para não entrar
// em loop com o próprio gate; exige sessão (o usuário está logado).
const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/change-password",
  component: ChangePasswordScreen,
});

// Cadastro obrigatório do mentorado (CPF). Fora do `_app`, como a troca de
// senha, para não entrar em loop com o próprio gate; exige sessão.
const completeProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/complete-profile",
  component: CompleteProfileScreen,
});

// Redefinicao por link enviado ao email. Publica porque o usuario ainda nao
// recuperou a sessao; o token de uso unico autoriza somente a troca da senha.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordScreen,
});

// Consentimento OAuth (cliente MCP pedindo acesso). Fora do `_app`: é uma
// tela cheia de decisão, sem sidebar nem guarda de papel. O back só manda para
// cá com a sessão já aberta, e o próprio endpoint de consentimento a exige.
const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/oauth/consent",
  component: OAuthConsentScreen,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: async () => {
    const session = await loadRouteSession();
    if (session.identity.role !== "mentorado" || usePreferencesStore.getState().viewAs)
      throw redirect({ to: "/" });
    return { session };
  },
  component: () => (
    <SessionGuard>
      <OnboardingPage />
    </SessionGuard>
  ),
  pendingComponent: AppLoadingScreen,
});

// Layout autenticado: guarda de sessão e política antes do render.
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  beforeLoad: async ({ location, preload }) => {
    if (!preload) {
      rememberFormRoute(location.pathname);
      rememberCommunicationRoute(location.pathname);
    }
    const session = await loadRouteSession();
    if (
      await loadOnboardingRequirement(
        session.identity,
        queryClient,
        usePreferencesStore.getState().viewAs,
      )
    )
      throw redirect({ to: "/onboarding", replace: true });
    if (
      !canEnterRoute(
        location.pathname,
        new Set(session.capabilities.map((capability) => capability.slug)),
        session.features,
        session.navigation,
      )
    ) {
      throw redirect({ to: "/" });
    }
    return { session };
  },
  component: () => (
    <SessionGuard>
      <AppLayout />
    </SessionGuard>
  ),
  loader: ({ context }) => loadShellNavigation(context.session),
  pendingComponent: AppLoadingScreen,
});

function screen<const T extends string>(path: T, component: RouteComponent) {
  return createRoute({ getParentRoute: () => appLayoutRoute, path, component });
}

const messagesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/messages",
  validateSearch: (search: Record<string, unknown>): { thread?: string } => ({
    thread: typeof search.thread === "string" && search.thread.trim() ? search.thread : undefined,
  }),
  component: MessagesRoute,
});

function MessagesRoute() {
  const { thread } = messagesRoute.useSearch();
  return <MessagesScreen key={thread ?? "list"} initialThreadId={thread} />;
}

const notificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/notifications",
  validateSearch: (search: Record<string, unknown>): { filter?: "unread" | "mentions" } => ({
    filter: search.filter === "unread" || search.filter === "mentions" ? search.filter : undefined,
  }),
  component: NotificationsRoute,
});

function NotificationsRoute() {
  const { filter } = notificationsRoute.useSearch();
  const navigate = notificationsRoute.useNavigate();
  return (
    <NotificationsScreen
      filter={filter ?? "all"}
      onFilterChange={(filter) => {
        void navigate({ search: { filter: filter === "all" ? undefined : filter }, replace: true });
      }}
    />
  );
}

// Todas as rotas são telas REAIS com dados do back (sem placeholder).
const appChildren = [
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/events",
    validateSearch: eventsSearch,
    component: EventsScreen,
  }),
  screen("/events/$id", EventScreen),
  screen("/admin/events", EventsManagement),
  screen("/admin/communications", CommunicationsManagement),
  screen("/communications/$id", CommunicationScreen),
  screen("/forms", FormsScreen),
  screen("/forms/$slug", FormScreen),
  screen("/support/help", SupportHelpScreen),
  screen("/support/help/$category", SupportHelpSectionScreen),
  screen("/support/tickets", SupportTicketsScreen),
  screen("/support/tickets/$id", SupportTicketScreen),
  screen("/", HomeScreen),
  screen("/journey", JourneyScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/getting-started",
    beforeLoad: ({ context }) => {
      if (context.session.identity.role !== "mentorado" || usePreferencesStore.getState().viewAs)
        throw redirect({ to: "/" });
    },
    component: GettingStartedScreen,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/diagnosis",
    beforeLoad: ({ context }) => {
      if (context.session.identity.role !== "mentorado" || usePreferencesStore.getState().viewAs)
        throw redirect({ to: "/" });
    },
    component: DiagnosisScreen,
  }),
  screen("/p/$slug", PageViewScreen),
  screen("/settings", SettingsScreen),
  screen("/profile", AccountProfileScreen),
  notificationsRoute,
  screen("/badges", MyAccessBadgesScreen),
  screen("/purchases", PurchasesScreen),
  screen("/admin/refunds", RefundsScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/badges/$badgeId/renew",
    component: lazyRouteComponent(() => import("@/billing/renewal-page"), "RenewalScreen"),
    validateSearch: (search: Record<string, unknown>) => ({
      order:
        typeof search.order === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(search.order)
          ? search.order
          : undefined,
      canceled: search.canceled === true || search.canceled === "true",
    }),
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/faq",
    beforeLoad: () => {
      throw redirect({ to: "/support/help" });
    },
  }),
  screen("/roles", RolesScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/programs",
    component: ProgramsScreen,
    validateSearch: withListSearch(programsSearch, ["create", "trail", "tab"]),
  }),
  screen("/programs/$id/view", ProgramViewScreen),
  screen("/courses/$id/builder", CourseBuilderScreen),
  screen("/courses/$id/certificate", CourseCertificateScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/courses/$id/view",
    component: CourseViewScreen,
    validateSearch: courseLearningSearch,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/courses",
    component: CoursesScreen,
    validateSearch: courseCatalogSearch,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/courses/library",
    component: CourseLibraryScreen,
    validateSearch: courseCatalogSearch,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/courses/$id/lessons/$lessonId",
    component: CoursePlayerScreen,
    validateSearch: courseLearningSearch,
  }),
  screen("/courses/$id", CoursePlayerScreen),
  screen("/spaces", SpacesScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/spaces/$id",
    component: SpacesDetailScreen,
    validateSearch: spaceFeedSearch,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/feed",
    component: FeedScreen,
    validateSearch: feedSearch,
  }),
  screen("/posts/$id", PostScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/mentions",
    beforeLoad: () => {
      throw redirect({ to: "/notifications", search: { filter: "mentions" }, replace: true });
    },
  }),
  screen("/saved-posts", SavedPostsScreen),
  screen("/users/$id", ProfileScreen),
  messagesRoute,
  screen("/showcase", ShowcaseScreen),
  screen("/showcase/$id", ShowcaseScreen),
  screen("/ranking", GamificationScreen),
  screen("/admin/gamification-badges", AdminGamificationBadgesScreen),
  screen("/admin", AdminDashboardScreen),
  screen("/wallet", WalletScreen),
  screen("/admin/features", AdminFeaturesScreen),
  screen("/company", CompanyScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/branding",
    validateSearch: withListSearch(brandingSearch, ["tab"]),
    component: AdminBrandingRoute,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/showcases",
    beforeLoad: redirectLegacyShowcases,
  }),
  screen("/admin/branding/vitrine/$id", ShowcaseBuilderScreen),
  screen("/admin/branding/vitrine/$id/preview", ShowcasePreviewScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/showcases/$id",
    beforeLoad: redirectLegacyShowcase,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/lesson-categories",
    validateSearch: lessonCategoriesSearch,
    component: LessonCategoriesScreen,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/diagnostics",
    validateSearch: diagnosticsSearch,
    component: DiagnosticsScreen,
  }),
  screen("/admin/diagnostics/new", DiagnosticEditorRoute),
  screen("/admin/diagnostics/$id", DiagnosticViewRoute),
  screen("/admin/diagnostics/$id/edit", DiagnosticEditorRoute),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/tags",
    validateSearch: adminTagsSearch,
    component: AdminTagsScreen,
  }),
  screen("/admin/sales", SalesScreen),
  screen("/admin/access-badges", AdminAccessBadgesScreen),
  screen("/admin/access-badges/$id", AdminAccessBadgeDetailScreen),
  screen("/admin/certificates", CertificateSettingsScreen),
  screen("/admin/certificates/models/$id", CertificateModelScreen),
  screen("/admin/ads", AdminAdsScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/moderation",
    validateSearch: adminModerationSearch,
    component: AdminModerationScreen,
  }),
  screen("/admin/audit", AdminAuditScreen),
  screen("/admin/lesson-comments", AdminLessonCommentsScreen),
  screen("/admin/users", AdminUsersScreen),
  screen("/admin/domains", AdminDomainsScreen),
  screen("/admin/insights", AdminInsightsScreen),
  screen("/admin/user-scores", AdminUserScoresScreen),
  screen("/admin/integrations", IntegrationsMarketplaceScreen),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/integrations/logs",
    component: IngestionLogsScreen,
    validateSearch: ingestionLogsSearch,
  }),
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/admin/integrations/direct",
    component: DirectIntegrationRoute,
    validateSearch: withListSearch(directIntegrationSearch, ["tab"]),
  }),
  screen("/admin/integrations/settings", AdminIntegrationsScreen),
  screen("/admin/institutional", AdminInstitutionalScreen),
  screen("/cohorts", CohortsScreen),
  screen("/mentees", MenteesScreen),
  screen("/analytics", AnalyticsScreen),
  screen("/analytics/diagnosis/$userId", DiagnosisPersonScreen),
  screen("/outcomes", OutcomesScreen),
  screen("/commitments", ExecutionScreen),
  screen("/certificates", MyCertificatesScreen),
  screen("/charges/$chargeId", ChargeReturnScreen),
  screen("/business", MyBusinessScreen),
];

const routeTree = rootRoute.addChildren([
  installRoute,
  authRoute,
  googleCallbackRoute,
  googleStartRoute,
  googleHandoffRoute,
  googleCompleteRoute,
  noAccessRoute,
  privacyRoute,
  termsRoute,
  lifecycleRoute,
  publicPageRoute,
  publicFormRoute,
  purchaseRoute,
  joinRoute,
  certificatePublicRoute,
  changePasswordRoute,
  completeProfileRoute,
  resetPasswordRoute,
  oauthConsentRoute,
  onboardingRoute,
  appLayoutRoute.addChildren(appChildren),
]);

export const router = createRouter({
  routeTree,
  ...routePreloadOptions,
  defaultPendingComponent: RouteLoadingScreen,
  defaultErrorComponent: RouteErrorScreen,
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  // Ao voltar/avançar no histórico, a janela retoma a rolagem daquela tela.
  scrollRestoration: true,
});
trackHistory(router.history);

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
